import { $enum } from 'ts-enum-util'
import { STOP_TYPE } from 'types/shipment'
import { IShipperSettings } from 'apps/tms/shipperSettings/utils/types/shipper-settings'
import {
  ShipperCarrierBasic,
  EtaUpdatedByWithTransfixAttributes,
  LtlMetadata,
  ShipmentTracking,
  ShipmentDocuments,
  Settings,
  GetLocationsResponse,
  ShipperFacility,
} from 'apps/tms/types/apiTypes'
import {
  REJECT_AT_REASONS,
  SHIPMENT_STATUSES,
  SHIPMENT_SUB_STATUSES,
} from 'apps/tms/utils/constants/shipmentStatuses'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { TMS_EQUIPMENT } from 'constants/tmsEquipment'
import { APPOINTMENTS } from './draftShipments'

// SHIPMENT CREATE V2 RESPONSE TYPES

export type RateType = 'line_haul' | 'all_in'

export interface IMarketPlaceRateInfo {
  rate: string
  rateSource: string
  rateType: RateType
  isTtrEligible: boolean
}

export interface IShipmentItemLtlMetadataResponse {
  id?: number | null
  metadata?: {
    height?: number | null
    length?: number | null
    width?: number | null
    freightClass?: string | null
    nmfcCode?: string | null
  } | null
}

export interface IShipmentItemsResponse {
  id: number
  pickupAbsoluteStopSequence: number
  deliveryAbsoluteStopSequence: number
  commodity: string
  grossWeight: number
  netWeight: number
  quantity: number
  quantityUnits: string
  packagingCount: number
  packagingType: string
  internalPoReference: string
  customerPoReference: string
  bolReference: string
  instructions: string
  ltlMetadata?: IShipmentItemLtlMetadataResponse
}

export interface IShipmentStopContactResponse {
  id?: number
  name?: string | null
  phone?: string | null
  phoneExt?: string | null
  email?: string | null
  purpose?: string | null
  shipperId: number
}

export interface IShipmentStopAddressResponse {
  id: number | null
  addressee?: string | null
  addressLine1?: string
  addressLine2?: string | null
  city?: string
  stateCode?: string
  postalCode?: string
  countryCode?: string
  timezone?: AllowedTimezones
  website?: string | null
  hours?: string | null
  instructions?: string | null
  latitude?: string
  longitude?: string
}

export type AppointmentType = keyof typeof APPOINTMENTS
export type StopType = 'pickup' | 'delivery'

export interface IShipmentStopResponse {
  id: number
  stopType: StopType | STOP_TYPE
  arrivedOnTime: boolean | null
  absoluteStopSequence: number
  relativeStopSequence: number
  appointmentReference: string | null
  appointmentType: AppointmentType
  instructions: string | null
  referenceNumber: string
  startAt: string
  endAt: string
  shipperFacility?: ShipperFacility
  address: IShipmentStopAddressResponse
  contact: IShipmentStopContactResponse
  isDropTrailer: boolean
  arrivedAt: string | null
  eta: string | null
  etaUpdatedAt: string | null
  etaUpdatedByWithTransfixAttributes?: EtaUpdatedByWithTransfixAttributes
  leftAt: string | null
  timezone?: string
  ediStatusUpdate?: {
    metadata?: {
      carrierMessage: string | null
      banyanMessage: string | null
      ediCode: string | null
    }
  }
}

export interface ShipmentStopArrivalStatus
  extends Pick<IShipmentStopResponse, 'id' | 'arrivedOnTime'> {}

export enum LINE_ITEM_AMOUNT_TYPES {
  allIn = 'all_in',
  lineHaul = 'line_haul',
  fuelSurcharge = 'surcharge',
  accessorial = 'accessorial',
}

export interface IShipmentRatesResponse {
  allIn: number
  lineItems: ILineItemResponse[]
}

export interface ISurchargeDetailResponse {
  fuelPricePerGallon: number
  miles: number
  surchargePerMile: number
  totalAmount: number
}

export type AccessorialDisplayCategory =
  | 'payable_to_carrier'
  | 'billable_to_carrier'
  | 'ltl_shipper'
  | 'ltl_consignee'
  | 'ltl_load'

export interface ILineItemResponse {
  id?: string
  amount: string
  amountType: LINE_ITEM_AMOUNT_TYPES
  title: string
  description?: string
  displayCategory: AccessorialDisplayCategory
  surchargeDetail?: ISurchargeDetailResponse
}

export enum MILES_SOURCE_TYPES {
  calculatedByGoogle = 'calculated_by_google',
  providedByShipper = 'provided_by_shipper',
}

export interface IStateTransition {
  note?: string
  toState: SHIPMENT_STATUSES | SHIPMENT_SUB_STATUSES
  createdAt?: string
  createdBy?: {
    id: number
    email: string
    name: string
  }
}

export interface ICoverageTypes {
  directAssignments: IDirectAssignmentCoverage[]
  routingGuideCoverage?: IRoutingGuideCoverage
  auction?: IAuctionCoverage
}

export interface IShipmentLTLResponse {
  proNumber: string
  invoiceId: string
  billingId: string
}

export enum SHIPMENT_SOURCE_TYPES {
  internal = 'internal',
  externalAPI = 'external_api',
  marketplace = 'marketplace',
}

export interface IShipmentResponse {
  id: number
  invoice?: {
    id: number
  }
  acceptedAt?: string
  shipmentType?: string
  auctionId?: string
  coordinatorId?: number
  rejectedAt?: string
  rejectReason?: REJECT_AT_REASONS
  rates?: IShipmentRatesResponse
  shipperId: number
  status: SHIPMENT_STATUSES
  subStatus: SHIPMENT_SUB_STATUSES
  externalId: string
  equipmentKey: AllTrailerTypes
  miles: number
  milesSource: string
  shipperLaneId?: string
  poReference: string | null
  bolReference: string | null
  instructions: string | null
  transfixLtlQuote: boolean
  rate: number
  rateType: RateType
  reeferPrecoolTemp: number | null
  reeferFixedTemp: number | null
  reeferMinTemp: number | null
  reeferMaxTemp: number | null
  shipperCarrierId: number | null
  shipperCarrier?: ShipperCarrierBasic
  shipper?: {
    /**
     * When true, the shipper should have access to Freight Audit And Pay features
     */
    isFapEnabled: boolean
    name: string
  }
  totalRate: number
  stops: IShipmentStopResponse[]
  items?: IShipmentItemsResponse[]
  lineItems?: ILineItemResponse[]
  accessorials?: ILineItemResponse[]
  coverage?: ICoverageTypes
  lastStateTransition?: IStateTransition
  /**
   * When shipment is public locations are included as part of the shipment response
   */
  locations?: GetLocationsResponse
  isDrivingTeamRequired: boolean | null
  sendManualTrackingReminders?: boolean
  p44Metadata?: {
    trackViaIntegration: boolean | null
  } | null
  receivesAutomaticTrackingUpdates: boolean | null
  currentAbsoluteStopSequence: number | null
  isDropTrailer?: boolean
  billingInstructions?: string
  /**
   * The truck number that we've sent to P44
   */
  truckNumber: string | null
  /**
   * This will be true if the truck number entered by the user is not set up to
   * connect to P44.
   */
  isTruckNumberValid: boolean | null
  tracking?: ShipmentTracking
  documents?: ShipmentDocuments
  ltlMetadata?: LtlMetadata
  source?: SHIPMENT_SOURCE_TYPES
  publicUuid?: string
  isHot?: boolean | null
  /**
   * Only flatbed related equipmentKey can be over dimensional.
   * If so, the user must specify the trailer Length, Width and Height respectively.
   */
  isOverDimensional?: boolean
  trailerLengthInches?: number
  trailerWidthInches?: number
  trailerHeightInches?: number
  isFoodGrade?: boolean
  /**
   * Name of driver transporting shipment
   */
  driverName?: string
  /**
   * Trailer number tied to shipment
   */
  trailerNumber?: string
  /**
   * copied from apiTypes, 'variable' means is cost plus
   */
  pricingType?: 'variable' | 'fixed' | null
}

/**
 * TODO: Swap this out with the generated PublicShipment type, and see what
 * breaks
 */
interface SourceAgnosticShipmentDetails
  extends Omit<IShipmentResponse, 'totalRate' | 'rate' | 'rateType' | 'transfixLtlQuote'> {}

/**
 * TODO: (later) use this as a guide for what to add to the authenticated
 * shipment details endpoint schema
 */
interface ShipmentDetailsRatesProps
  extends Partial<
    Pick<IShipmentResponse, 'totalRate' | 'rate' | 'rateType' | 'transfixLtlQuote'>
  > {}

export type ShipmentDetails = SourceAgnosticShipmentDetails & ShipmentDetailsRatesProps

export interface IAuctionCoverage {
  id: string
  settings: IShipperSettings
}

// We only need to know whether this exists for now, fill in this type with
// more details when needed.
export interface IRoutingGuideCoverage {
  id: string
  carrierAssignments?: ICarrierAssignmentCoverage[]
  settings: {
    auctionParams?: IShipperSettings
  }
}

export interface IDirectAssignmentCoverage {
  shipperCarrier: ShipperCarrierBasic
  createdAt: string
  expireAt: string
  rate: number
  rateType: RateType
  acceptedAt?: string
  rejectedAt?: string
  rejectReason?: string
}

export interface ICarrierAssignmentCoverage {
  shipperCarrier: ShipperCarrierBasic
  createdAt: string
  expireAt: string
  rate: number
  rateType: RateType
  routingGuideCoverageId: number
  acceptedAt?: string
  rejectedAt?: string
  rejectReason?: string
}

// SHIPMENT CREATE V2 ENUMS

export enum TEMP_TYPES {
  'Fixed' = 'Fixed Temperature',
  'Range' = 'Continuous Flow',
}

export enum EQUIPMENT_TYPES {
  'dry_van' = 'Dry Van',
  'reefer' = 'Reefer',
  'open_deck' = 'Flatbed',
}

// This is only used when Intermodal feature `tmsIntermodal` is enabled
export enum EQUIPMENT_INTERMODAL_TYPES {
  'container' = 'Container',
  'mixed_unspecified' = 'Mixed / Unspecified',
}

export enum MODE_TYPES {
  'ftl' = 'Full Truckload',
  'ltl' = 'Less Than Truckload',
}

// This is only used when Intermodal feature `tmsIntermodal` is enabled
export enum MODE_INTERMODAL_TYPES {
  'intermodal' = 'Intermodal',
}

export enum FLATBED_TRAILER_TYPES {
  '53_flatbed' = "53' Flatbed",
  '48_flatbed' = "48' Flatbed",
  '45_flatbed' = "45' Flatbed",
  '42_flatbed' = "42' Flatbed",
  '40_flatbed' = "40' Flatbed",
  'extendable_flatbed' = 'Extendable Flatbed',
  'double_drop' = 'Double Drop',
  'stepdeck' = 'Stepdeck',
}

export enum REEFER_TRAILER_TYPES {
  '53_reefer' = "53' Reefer",
  '51_reefer' = "51' Reefer",
  '48_reefer' = "48' Reefer",
  '40_reefer' = "40' Reefer",
  '36_reefer' = "36' Reefer",
  '32_reefer' = "32' Reefer",
  '28_reefer' = "28' Reefer",
  'space_saver_reefer' = 'Space Saver Reefer',
  'ice_cream_trailer' = 'Ice Cream Trailer',
}

export enum DRY_VAN_TRAILER_TYPES {
  '53_van' = "53' Dry Van",
  '48_van' = "48' Dry Van",
  '45_van' = "45' Dry Van",
  '43_van' = "43' Dry Van",
  '42_van' = "42' Dry Van",
  '40_van' = "40' Dry Van",
  '36_van' = "36' Dry Van",
  '32_van' = "32' Dry Van",
  '28_van' = "28' Dry Van",
  '20_van' = "20' Dry Van",
  'vented_van' = 'Vented Van',
  'plate_trailer' = 'Plate Trailer',
}

export enum CONTAINER_TYPES {
  '53_container' = "53' Container",
  '48_container' = "48' Container",
  '45_container' = "45' Container",
  '40_container' = "40' Container",
  '20_container' = "20' Container",
  'bulk_tanker' = 'Bulk Tanker',
  'dry_storage_container' = 'Dry Storage Container',
  'flat_rack_container' = 'Flat Rack Container',
  'half_height_containers' = 'Half Height Containers',
  'iso_tanks' = 'ISO Tanks',
  'open_side_storage_container' = 'Open Side Storage Container',
  'open_top_container' = 'Open Top Container',
  'refrigerated_iso_containers' = 'Refrigerated ISO Containers',
  'special_purpose_containers' = 'Special Purpose Containers',
  'tanker' = 'Tanker',
}

export const TRAILER_TYPES = {
  ...FLATBED_TRAILER_TYPES,
  ...REEFER_TRAILER_TYPES,
  ...DRY_VAN_TRAILER_TYPES,
}

export const TRAILER_TYPES_WITH_INTERMODAL_TYPES = {
  ...FLATBED_TRAILER_TYPES,
  ...REEFER_TRAILER_TYPES,
  ...DRY_VAN_TRAILER_TYPES,
  ...CONTAINER_TYPES,
}

export const ALL_TRAILER_TYPES = [
  ...$enum(DRY_VAN_TRAILER_TYPES).getKeys(),
  ...$enum(REEFER_TRAILER_TYPES).getKeys(),
  ...$enum(FLATBED_TRAILER_TYPES).getKeys(),
  ...$enum(CONTAINER_TYPES).getKeys(),
]

export type TempControlTypes = keyof typeof TEMP_TYPES
export type EquipmentTypes = keyof typeof EQUIPMENT_TYPES
export type EquipmentTypesIntermodal = keyof typeof EQUIPMENT_INTERMODAL_TYPES
export type DryVanTrailerTypes = keyof typeof DRY_VAN_TRAILER_TYPES
export type ReeferTrailerTypes = keyof typeof REEFER_TRAILER_TYPES
export type FlatbedTrailerTypes = keyof typeof FLATBED_TRAILER_TYPES
export type AllTrailerTypes =
  | keyof typeof FLATBED_TRAILER_TYPES
  | keyof typeof REEFER_TRAILER_TYPES
  | keyof typeof DRY_VAN_TRAILER_TYPES
  | keyof typeof CONTAINER_TYPES

export enum COVERAGE_TYPES {
  'routing_guide' = 'Tender by Routing Guide',
  'auction' = 'Use Capacity Finder',
  'direct_assignment' = 'Direct Tender',
}

export interface SettingsWithCoverageOptions
  extends Settings,
    Partial<Omit<IMarketPlaceRateInfo, 'rateType'>> {
  carriers?: Settings['auctionCarrierGroup']
  autoAccept?: boolean
  shipperCarrierId?: string
  // The following are required for an LTL shipment
  carrierIdentifier?: string
  quoteId?: number
  transitTime?: number
  carrierIds?: number[]
}

export interface ICoverage {
  coverageType?: string
  carrierId?: string
  auctionParams?: SettingsWithCoverageOptions
  routingGuideParams?: {
    auctionParams: SettingsWithCoverageOptions
  }
}

export enum PACKAGING_TYPE {
  'palletized' = 'Pallets',
  'floor_loaded' = 'Floor Loaded',
  'other' = 'Other',
}

export interface IShipmentDetails {
  rate: number
  acceptedAt: string
  rejectedAt: string
  rejectReason: string
  rateType: RateType
  equipmentKey: TMS_EQUIPMENT
  commodity: string
  fixedTemp?: string
  maxTemp?: string
  minTemp?: string
  precoolTemp?: string
  shipmentWeight: number
  packagingType: PACKAGING_TYPE
  palletCount: string
  miles: number
  status: SHIPMENT_STATUSES
  shipmentId: string
  pickup: IShipmentStop
  delivery: IShipmentStop
  carriers: IShipmentCarrier[]
}

export interface ICarrierShipmentDetails extends IShipmentDetails {
  rate: number
  acceptedAt: string
  rejectedAt: string
  rejectReason: string
  rateType: RateType
}

export interface IShipperShipmentDetails extends IShipmentDetails {
  auctionId: string
}

export interface IShipmentTitle {
  pickupCity?: string
  pickupStateCode?: string
  deliveryCity?: string
  deliveryStateCode?: string
  firstPickupDate?: string
  lastDeliveryDate?: string
  miles?: number
  shipmentId?: string
  externalId?: string
}

export interface IShipmentStop {
  facilityName: string
  address: string
  city: string
  stateCode: string
  postalCode: string
  referenceNumber: string
  appointmentTimeStart?: string
  appointmentTimeEnd?: string
  appointmentType?: string
  displayTimezone?: string
  specialInstructions?: string
}

export interface IShipmentCarrier {
  id?: string
  rank?: number
  name: string
  contractRate?: string
  contractRateType: RateType
  status?: string
  timeRemaining?: string
}

export const tmsStopTypeMapping = {
  pickup: STOP_TYPE.pickup,
  delivery: STOP_TYPE.delivery,
}

export type TShipmentEtaStatus = 'onTime' | 'late' | 'unavailable'
